import React, { useCallback, useEffect, useState } from 'react';
import { Approval, ApprovalQuery, reportReviewService } from '../../services/reportReview.service';
import { formatApprovalTime, isLegacyPeriod } from './approvalTime';
import './WriteCompleteBar.css';

const REVIEWER_DEMO_MESSAGE = 'demo를 위한 기능은 여기까지 제공됩니다. 실제 데이터는 저장되지 않습니다.';

interface Props {
  /** 기간·회사(계열)가 다 골라지지 않았으면 null */
  query: ApprovalQuery | null;
  role: string | undefined;
  /** 작성완료 직전에 화면의 미저장 내용(요약 등)을 저장한다. 실패하면 작성완료하지 않는다 */
  beforeComplete?: () => Promise<void>;
}

const errorText = (e: any, fallback: string): string => e?.response?.data?.message || fallback;

/**
 * 관리자 보고서 화면의 「작성완료」 — 기존 PUBLISH 자리 (report-approval-flow SPEC §6.1).
 * 발행하지 않는다: 발행(Viewer 공개)은 작성/검토이력의 월 승인으로만 일어난다.
 */
const WriteCompleteBar: React.FC<Props> = ({ query, role, beforeComplete }) => {
  const [approval, setApproval] = useState<Approval | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const legacy = query ? isLegacyPeriod(query.period) : false;
  const key = query && !legacy ? JSON.stringify(query) : null;

  const load = useCallback(async () => {
    if (!key) return;
    try {
      setApproval(await reportReviewService.getApproval(JSON.parse(key) as ApprovalQuery));
    } catch (e) {
      setApproval(null);
      setError(errorText(e, '작성 상태를 불러오지 못했습니다.'));
    }
  }, [key]);

  useEffect(() => {
    setApproval(null);
    setError(null);
    load();
  }, [load]);

  if (!query) return null;
  if (legacy) {
    return (
      <div className="write-complete-bar">
        <p className="write-complete-bar__note">202608 이전 보고서는 기존 발행 방식으로 공개되었습니다.</p>
      </div>
    );
  }

  const written = !!approval?.writtenAt;
  const approved = !!approval?.approvedAt;

  const run = async (confirmText: string, action: () => Promise<void>, fallback: string) => {
    if (role === 'reviewer') {
      window.alert(REVIEWER_DEMO_MESSAGE);
      return;
    }
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError(errorText(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const complete = () =>
    run(
      '작성완료 처리합니다. 보고서 공개는 관리자 승인 시 그달 보고서와 함께 일괄로 이뤄집니다.',
      async () => {
        await beforeComplete?.();
        await reportReviewService.markWritten(query);
      },
      '작성완료 처리에 실패했습니다.',
    );
  const cancel = () =>
    run('작성완료를 취소하시겠습니까?', () => reportReviewService.cancelWritten(query), '작성완료 취소에 실패했습니다.');

  return (
    <div className="write-complete-bar">
      {error && (
        <p className="write-complete-bar__error" role="alert">
          {error}
        </p>
      )}
      {written && (
        <span className="write-complete-bar__status">
          작성완료 · {approval?.writer} {formatApprovalTime(approval?.writtenAt ?? null)}
        </span>
      )}
      {approved ? (
        <button type="button" className="btn-secondary" disabled>
          승인완료
        </button>
      ) : written ? (
        <button type="button" className="btn-secondary" onClick={cancel} disabled={busy || !approval}>
          작성완료 취소
        </button>
      ) : (
        <button type="button" className="btn-primary write-complete-bar__primary" onClick={complete} disabled={busy || !approval}>
          {busy ? '처리 중...' : '작성완료'}
        </button>
      )}
    </div>
  );
};

export default WriteCompleteBar;
